/**
 * Membership Slot Pricing
 *
 * - Plan rules come from window.MembershipPlans: only legacy Unlimited blocks weekends and has a
 *   booking window; Starter/Pro/Elite/Executive draw from an hour pool, Elite/Executive on top of
 *   that are capped per facility-local day.
 * - Fri/Sat/Sun bookings bill the day's own variant price for everyone; the member's benefit is
 *   the tier percent on whatever hours did not cover, gated by resolveTierEligibility.
 * - Hours already spent by other cart items must be read from the live cart, per period and per
 *   day, so two line items can't double-spend the same balance.
 * - The backend has the final word: a hold answers with membership_consumable_hours, which caps
 *   what the cart property may claim.
 *
 * Depends on window.MembershipHoursUtils for the underlying period/discount math.
 *
 */
(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.MembershipSlotPricing) return;

  const PERCENT_MAX = 100;
  const DISCOUNT_DECIMALS = 4;
  /** Day.js-style 1 = Monday … 7 = Sunday, matching A9DateUtils.weekday1to7. */
  const WEEKDAY_MON = 1;
  const WEEKDAY_FRI = 5;
  const PREFERENCE_SOURCE_RANK = { account: 1, grid: 2 };
  const pendingPreferenceLookups = new Set();

  function getWeekday1to7(dateStr, facilityTimeZone) {
    const dateUtils = window.A9DateUtils;
    return dateUtils && dateUtils.hasTz && dateUtils.hasTz()
      ? dateUtils.weekday1to7(dateStr, facilityTimeZone)
      : null;
  }

  function getProMembershipPeriodKey(period) {
    if (!period) return null;
    const facilityId = period.facility_id != null ? Number(period.facility_id) : null;
    if (facilityId == null || !period.start) return null;
    return `${facilityId}|${period.start}`;
  }

  /** Membership for this facility on this day — a period can end mid-calendar. */
  function getMembershipCoverageForDate(membershipData, facilityId, dateStr, facilityTimeZone, preferredType) {
    const coverage = { active: false, type: null, periodStart: null, endYmd: null };
    const utils = window.MembershipHoursUtils;
    if (!utils || !membershipData || !facilityId || !dateStr) return coverage;
    if (!utils.hasMembershipSlotData(membershipData, facilityId)) return coverage;

    if (!utils.hasStructuredMembershipRows(membershipData)) {
      const pool = Number(utils.getLegacyFacilityMapHours(membershipData, facilityId));
      if (!(pool > 0)) return coverage;
      coverage.active = true;
      const plans = window.MembershipPlans;
      coverage.type = plans && !plans.hasHourPool({ hours: pool }) ? 'unlimited' : 'pro';
      return coverage;
    }

    const entries = utils.getMembershipEntries(membershipData);
    const period = utils.getPeriodForFacilityAndDate(entries, facilityId, dateStr, facilityTimeZone, preferredType);
    if (!period) return coverage;

    coverage.active = true;
    coverage.type = utils.isUnlimitedType(period) ? 'unlimited' : 'pro';
    coverage.periodStart = period.start || null;
    coverage.endYmd = utils.getPeriodEndYmd(entries, period, facilityTimeZone) || null;
    return coverage;
  }

  function getMembershipEligibilityForSlot(membershipData, facilityId, dateStr, facilityTimeZone) {
    const eligibility = { weekendNotFree: false };
    const utils = window.MembershipHoursUtils;
    if (!utils || !membershipData || !facilityId || !dateStr) return eligibility;
    if (!utils.hasStructuredMembershipRows(membershipData)) return eligibility;

    const entries = utils.getMembershipEntries(membershipData);
    const noPoolPeriod = utils.getPeriodForFacilityAndDateByType(entries, facilityId, dateStr, 'unlimited', facilityTimeZone);
    const poolPeriod = utils.getPeriodForFacilityAndDateByType(entries, facilityId, dateStr, 'pro', facilityTimeZone);
    if (!noPoolPeriod && !poolPeriod) return eligibility;

    const weekday1to7 = getWeekday1to7(dateStr, facilityTimeZone);
    const isWeekendForDiscount = weekday1to7 != null ? !(weekday1to7 >= WEEKDAY_MON && weekday1to7 <= WEEKDAY_FRI) : true;

    const plans = window.MembershipPlans;
    const noPoolPlanBlocksWeekends = !!(noPoolPeriod && (!plans || plans.forbidsWeekends(noPoolPeriod)));

    eligibility.weekendNotFree = isWeekendForDiscount && noPoolPlanBlocksWeekends && !poolPeriod;
    return eligibility;
  }

  /**
   * Whether the member's tier percent may be charged on a booking at this facility on this date.
   *
   * The percent is a membership benefit, so it lives inside the membership's own period and its
   * own facility — a Pro member at Cincinnati gets nothing on an Atlanta booking, and nothing on
   * a date no period covers. Network-wide plans pass the facility test anywhere;
   * `getMembershipCoverageForDate` already encodes both rules.
   *
   * Written into `_membership_hours.tierEligible` and read by the SupaEasy discount function,
   * which cannot see the customer metafield. The percent itself comes from the customer tag
   * there, so a tampered flag can only ever move a member's own rate to the wrong booking.
   */
  function resolveTierEligibility({ membershipData, facilityId, dateStr, facilityTimeZone, preferredType, accountMembershipActive }) {
    if (!dateStr || !facilityId) return !!accountMembershipActive;

    const utils = window.MembershipHoursUtils;
    if (utils && membershipData && utils.hasMembershipSlotData(membershipData, facilityId)) {
      return getMembershipCoverageForDate(membershipData, facilityId, dateStr, facilityTimeZone, preferredType).active;
    }
    // No rows for this facility: the account API only knows "active now", which is the best
    // answer available on that path.
    return !!accountMembershipActive;
  }

  /**
   * The member's tier percent, and what it takes off `priceCents`.
   *
   * Mirrors the SupaEasy function exactly: the rate comes from the customer tag (emitted as
   * window.A9MemberTier), the right to charge it from resolveTierEligibility, and it lands on
   * what is still owed after membership hours. Anything the popup or the summary shows has to
   * come through here, or the quoted price and the charged price drift apart.
   */
  function resolveTierDiscount({ priceCents, membershipData, facilityId, dateStr, facilityTimeZone, preferredType, accountMembershipActive }) {
    const none = { percent: 0, label: '', offCents: 0 };
    const tier = typeof window !== 'undefined' ? window.A9MemberTier : null;
    const percent = tier ? Number(tier.percent) : 0;
    if (!(percent > 0) || !(priceCents > 0)) return none;

    const eligible = resolveTierEligibility({
      membershipData, facilityId, dateStr, facilityTimeZone, preferredType, accountMembershipActive
    });
    if (!eligible) return none;

    return {
      percent,
      label: String(tier.label || ''),
      offCents: Math.round((priceCents * percent) / PERCENT_MAX)
    };
  }

  function readMembershipPreference(facilityId) {
    const preference = typeof window !== 'undefined' ? window.A9MembershipPreference : null;
    if (!preference || !preference.planKey || !facilityId) return null;
    return Number(preference.facilityId) === Number(facilityId) ? preference.planKey : null;
  }

  function preferenceRank(source) {
    return PREFERENCE_SOURCE_RANK[source] || PREFERENCE_SOURCE_RANK.account;
  }

  function publishMembershipPreference(facilityId, planKey, source) {
    if (typeof window === 'undefined' || !planKey || !facilityId) return false;

    const current = window.A9MembershipPreference;
    const sameFacility = !!current && Number(current.facilityId) === Number(facilityId);
    if (sameFacility && preferenceRank(source) < preferenceRank(current.source)) return false;

    const unchanged = sameFacility && current.planKey === String(planKey);
    window.A9MembershipPreference = {
      facilityId: Number(facilityId),
      planKey: String(planKey),
      source: PREFERENCE_SOURCE_RANK[source] ? source : 'account'
    };

    if (unchanged) return false;

    if (typeof document !== 'undefined' && typeof CustomEvent === 'function') {
      document.dispatchEvent(new CustomEvent('membership:preference', { detail: window.A9MembershipPreference }));
    }
    return true;
  }

  function beginMembershipPreferenceLookup(facilityId) {
    if (facilityId) pendingPreferenceLookups.add(Number(facilityId));
  }

  function endMembershipPreferenceLookup(facilityId) {
    if (!facilityId || !pendingPreferenceLookups.delete(Number(facilityId))) return;
    if (typeof document !== 'undefined' && typeof CustomEvent === 'function') {
      const detail = typeof window !== 'undefined' ? window.A9MembershipPreference || null : null;
      document.dispatchEvent(new CustomEvent('membership:preference', { detail }));
    }
  }

  function isMembershipPreferencePending(facilityId) {
    return !!facilityId && pendingPreferenceLookups.has(Number(facilityId));
  }

  function resolvePreferredType(facilityId, gridMeta) {
    return (gridMeta && gridMeta.planKey) || readMembershipPreference(facilityId) || null;
  }

  function readMembershipRowFacilityId(row) {
    const facilityId = Number(row?.facility_id ?? row?.facilityId ?? row?.facility?.id);
    return Number.isFinite(facilityId) ? facilityId : null;
  }

  function rankAccountMemberships(rows, facilityId) {
    const plans = window.MembershipPlans;
    if (!Array.isArray(rows) || !facilityId) return [];

    return rows
      .filter((row) => {
        if (row?.status !== 'active') return false;
        const rowFacilityId = readMembershipRowFacilityId(row);
        if (rowFacilityId === null) {
          console.warn('Membership row without a facility id skipped — cannot confirm it belongs to this location', row);
          return false;
        }
        return rowFacilityId === Number(facilityId) || !!(plans && plans.isCrossFacility(row));
      })
      .sort((a, b) => {
        const aHome = readMembershipRowFacilityId(a) === Number(facilityId);
        const bHome = readMembershipRowFacilityId(b) === Number(facilityId);
        if (aHome !== bHome) return aHome ? -1 : 1;
        return Number(b?.id || 0) - Number(a?.id || 0);
      });
  }

  /** The membership period covering a date, or null — the one answer to "is this date covered?". */
  function getCoveringPeriod(membershipData, facilityId, dateStr, facilityTimeZone, preferredType) {
    const utils = window.MembershipHoursUtils;
    if (!utils || !membershipData || !facilityId || !dateStr) return null;
    if (!utils.hasMembershipSlotData(membershipData, facilityId)) return null;
    return utils.getMembershipHoursForDate(membershipData, facilityId, dateStr, 0, facilityTimeZone, preferredType).period;
  }

  function getMembershipHoursForDate(membershipData, facilityId, dateMdy, durationHours, facilityTimeZone, suiteCount, preferredType) {
    const utils = window.MembershipHoursUtils;
    if (!utils || !membershipData || !facilityId || !dateMdy || !(durationHours > 0)) return null;
    if (!utils.hasMembershipSlotData(membershipData, facilityId)) return null;

    const suites = Number(suiteCount) > 0 ? Number(suiteCount) : 1;
    const totalMembershipHoursNeeded = durationHours * suites;

    const eligibility = getMembershipEligibilityForSlot(membershipData, facilityId, dateMdy, facilityTimeZone);
    if (eligibility.weekendNotFree) return null;

    const info = utils.getMembershipHoursForDate(membershipData, facilityId, dateMdy, 0, facilityTimeZone, preferredType);

    // No date-level cap for plans without one; with a cap the estimate can't exceed a single day.
    const dailyCap = info.dailyLimit != null ? info.dailyLimit : Infinity;

    if (!info.hasHourPool) {
      const usedHours = Math.min(totalMembershipHoursNeeded, dailyCap);
      return usedHours > 0 ? { usedHours, isUnlimited: true, period: info.period } : null;
    }

    if (info.hours == null || info.hours <= 0) return null;

    const usedHours = Math.min(info.hours, totalMembershipHoursNeeded, dailyCap);
    return usedHours > 0 ? { usedHours, isUnlimited: false, period: info.period } : null;
  }

  /**
   * Membership hours other cart items already spend:
   * `byPeriod` guards the period balance, `byDay` guards the plan's daily cap.
   */
  async function getMembershipHoursUsedInCart(facilityTimeZone) {
    const usedHoursByPeriod = {};
    const usedHoursByDay = {};
    const utils = window.MembershipHoursUtils;
    const plans = window.MembershipPlans;

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return { byPeriod: usedHoursByPeriod, byDay: usedHoursByDay };
      const cart = await response.json();
      if (!cart || !Array.isArray(cart.items)) return { byPeriod: usedHoursByPeriod, byDay: usedHoursByDay };

      const addPeriodHours = (period, usedHours) => {
        if (!period || usedHours <= 0) return;
        if (plans && !plans.hasHourPool(period)) return;
        const key = getProMembershipPeriodKey(period);
        if (!key) return;
        usedHoursByPeriod[key] = (usedHoursByPeriod[key] || 0) + usedHours;
      };

      const addDayHours = (period, usedHours, bookingStartZulu) => {
        if (!period || usedHours <= 0 || !utils || !bookingStartZulu) return;
        if (plans && plans.dailyLimitFor(period) == null) return;
        const ymd = utils.ymdForInstantInZone(bookingStartZulu, facilityTimeZone);
        const key = utils.membershipDayKey(period.facility_id, ymd);
        if (!key) return;
        usedHoursByDay[key] = (usedHoursByDay[key] || 0) + usedHours;
      };

      const addDaysUsed = (period, daysUsed) => {
        if (!period || !daysUsed || !utils) return false;
        if (plans && plans.dailyLimitFor(period) == null) return true;
        let counted = false;
        Object.keys(daysUsed).forEach((ymd) => {
          const hours = Number(daysUsed[ymd] || 0);
          if (hours <= 0) return;
          const key = utils.membershipDayKey(period.facility_id, ymd);
          if (!key) return;
          usedHoursByDay[key] = (usedHoursByDay[key] || 0) + hours;
          counted = true;
        });
        return counted;
      };

      cart.items.forEach((item) => {
        let membershipHours = item && item.properties ? item.properties._membership_hours : null;
        if (typeof membershipHours === 'string') {
          try {
            membershipHours = JSON.parse(membershipHours.replaceAll(/&quot;/g, '"').replaceAll(/&#39;/g, "'").trim());
          } catch (error) {
            membershipHours = null;
          }
        }
        if (!membershipHours || membershipHours.status !== 'success') return;

        const bookingStartZulu = item.properties ? item.properties._time_from : null;
        const periodsUsed = Array.isArray(membershipHours.periodsUsed) ? membershipHours.periodsUsed : null;
        const primaryPeriod = (periodsUsed && periodsUsed[0] && periodsUsed[0].period) || membershipHours.period;
        // Items written before daysUsed existed fall back to charging the whole booking to its start day.
        const daysCounted = addDaysUsed(primaryPeriod, membershipHours.daysUsed);

        if (periodsUsed && periodsUsed.length) {
          periodsUsed.forEach((periodUsage) => {
            const period = periodUsage && periodUsage.period;
            const usedHours = Number((periodUsage && periodUsage.usedHours) || 0);
            addPeriodHours(period, usedHours);
            if (!daysCounted) addDayHours(period, usedHours, bookingStartZulu);
          });
          return;
        }

        const usedHours = Number(membershipHours.usedHours || 0);
        addPeriodHours(membershipHours.period, usedHours);
        if (!daysCounted) addDayHours(membershipHours.period, usedHours, bookingStartZulu);
      });
    } catch (error) {
      return { byPeriod: usedHoursByPeriod, byDay: usedHoursByDay };
    }

    return { byPeriod: usedHoursByPeriod, byDay: usedHoursByDay };
  }

  /**
   * A consumable-hours figure from the backend. `null` = unknown (no identity, no membership, a
   * degraded request) and the caller falls back to the local estimate; `0` = it covers nothing.
   */
  function readConsumableHours(value) {
    if (value === null || value === undefined || value === '') return null;
    const hours = Number(value);
    return Number.isFinite(hours) ? hours : null;
  }

  /**
   * Scalar min() of the local estimate and the backend figure.
   * null keeps the estimate; 0 covers nothing. Don't sum them — each already nets out other cart items.
   */
  function reconcileConsumableHours(localHours, backendHours) {
    const backend = readConsumableHours(backendHours);
    if (backend === null) return { hours: localHours, source: 'estimate', authoritativeZero: false };
    return { hours: Math.min(localHours, backend), source: 'backend', authoritativeZero: backend === 0 };
  }

  /**
   * `meta.membership` of a GET /slots response, normalized.
   * null = the backend was not asked or could not answer; `{ active: false }` = asked, no membership.
   */
  function readSlotsMembershipMeta(slotsPayload) {
    const api = window.SlotApiService;
    if (!api || typeof api.hasMembershipMeta !== 'function') return null;
    if (!api.hasMembershipMeta(slotsPayload)) return null;

    const membership = api.readMembershipMeta(slotsPayload);
    if (!membership) return { active: false };

    return {
      active: true,
      planKey: membership.plan || null,
      crossFacility: membership.cross_facility === true,
      dailyLimit: readConsumableHours(membership.daily_limit),
      buckets: Array.isArray(membership.buckets) ? membership.buckets : null,
      raw: membership
    };
  }

  /**
   * Membership fields of a hold response. The backend knows the customer's other bookings, so
   * `consumableHours` is the authoritative ceiling for what the cart may claim.
   */
  function readHoldMembership(holdResponse) {
    const result = { applied: false, consumableHours: null, rejectionReason: null };
    if (!holdResponse) return result;

    result.applied = holdResponse.membership != null;
    result.consumableHours = readConsumableHours(holdResponse.membership_consumable_hours);
    result.rejectionReason = holdResponse.membership_rejection_reason || null;
    return result;
  }

  /**
   * Round each part and put the rounding drift on the largest, so the parts always sum to `total`.
   * Rounding them independently can otherwise claim more hours than the line item spends.
   */
  function roundPartsToTotal(values, total, utils) {
    const rounded = values.map((value) => utils.roundHours(value));
    if (!rounded.length) return rounded;

    const drift = utils.roundHours(total - rounded.reduce((sum, value) => sum + value, 0));
    if (drift === 0) return rounded;

    let largest = 0;
    rounded.forEach((value, index) => { if (value > rounded[largest]) largest = index; });
    rounded[largest] = utils.roundHours(rounded[largest] + drift);
    return rounded;
  }

  /** Rescales per-period hours to a new total, parts still summing to the whole after rounding. */
  function scalePeriodsUsed(periodsUsed, fromTotal, toTotal, utils) {
    if (!(fromTotal > 0)) return periodsUsed.map(({ period }) => ({ period, usedHours: 0 }));

    const scaled = periodsUsed.map(({ usedHours }) => (usedHours * toTotal) / fromTotal);
    const rounded = roundPartsToTotal(scaled, toTotal, utils);
    return periodsUsed.map(({ period }, index) => ({ period, usedHours: rounded[index] }));
  }

  /**
   * Turns the grid/hold figure into a source of hours rather than only a cap — see
   * `applyBackendGrant` in membership-hours-utils.js. Only an explicit `active: true` qualifies:
   * `null` means the backend was not asked or could not answer, and must never grant anything.
   */
  function buildBackendGrant(membershipMeta, ceilingHours, gridFresh) {
    // The grant is not clamped by the cart tallies because the figure already nets the other items
    // out through their live holds — which only holds while the grid was fetched after the last cart
    // change. So freshness is a precondition, not a comment: a caller that cannot vouch for it (or
    // vouches negatively) gets no grant and falls back to the metafield estimate.
    if (gridFresh !== true) return null;
    if (!membershipMeta || membershipMeta.active !== true) return null;
    const hours = readConsumableHours(ceilingHours);
    if (hours === null || !(hours > 0)) return null;

    return {
      hours,
      type: membershipMeta.planKey || null,
      dailyLimit: membershipMeta.dailyLimit != null ? membershipMeta.dailyLimit : null,
      crossFacility: membershipMeta.crossFacility === true
    };
  }

  function buildMembershipHoursCartProperty({ membershipData, facilityId, dateStr, timeStr, durationHours, hoursUsedInCartByPeriod, hoursUsedInCartByDay, facilityTimeZone, suiteCount, consumableHours, gridConsumableHours, preferredType, membershipMeta, gridFresh }) {
    const utils = window.MembershipHoursUtils;
    if (!utils || !facilityId || !dateStr || !timeStr || !(durationHours > 0)) return null;

    // The hold answer wins; the grid figure backstops a hold response that omitted the field.
    const holdCeiling = readConsumableHours(consumableHours);
    const ceiling = holdCeiling !== null ? holdCeiling : readConsumableHours(gridConsumableHours);
    const backendGrant = buildBackendGrant(membershipMeta, ceiling, gridFresh);

    // Without rows for this facility there is nothing to price — unless the backend answered for
    // this very slot, which it can do for a date the rolling hours window no longer reaches.
    if (!backendGrant && (!membershipData || !utils.hasMembershipSlotData(membershipData, facilityId))) return null;

    const suites = Number(suiteCount) > 0 ? Number(suiteCount) : 1;
    const totalMembershipHoursNeeded = durationHours * suites;

    const hasStructuredRows = utils.hasStructuredMembershipRows(membershipData);
    const entries = hasStructuredRows ? utils.getMembershipEntries(membershipData) : [];
    const poolPeriod = entries.length ? utils.getPeriodForFacilityAndDateByType(entries, facilityId, dateStr, 'pro', facilityTimeZone) : null;
    const noPoolPeriod = entries.length ? utils.getPeriodForFacilityAndDateByType(entries, facilityId, dateStr, 'unlimited', facilityTimeZone) : null;

    // fullPriceCents is 1 because only the hours and the percentage are wanted here, not money.
    const discountResult = utils.calculateMembershipDiscountForSlot({
      membershipData,
      facilityId,
      dateStr,
      timeStr,
      durationHours,
      fullPriceCents: 1,
      hoursUsedByPeriod: hoursUsedInCartByPeriod || {},
      hoursUsedByDay: hoursUsedInCartByDay || {},
      facilityTimeZone,
      suiteCount: suites,
      consumableHoursCeiling: ceiling,
      preferredType: preferredType,
      backendGrant: backendGrant
    });

    const computedHours = utils.roundHours(discountResult.membershipHoursUsed || 0);
    // Engine already clamped; this min only stops rounding from exceeding the ceiling.
    const usedHours = utils.roundHours(Math.max(0, reconcileConsumableHours(computedHours, ceiling).hours));
    const discountPercent = totalMembershipHoursNeeded > 0 ? Math.max(0, Math.min(PERCENT_MAX, (usedHours / totalMembershipHoursNeeded) * PERCENT_MAX)) : 0;

    const grantedPeriod = discountResult.period || null;

    let periodsUsed = Array.isArray(discountResult.membershipHoursUsedByPeriod) ? discountResult.membershipHoursUsedByPeriod : [];
    if (discountResult.isUnlimited && !periodsUsed.length && usedHours > 0 && (grantedPeriod || noPoolPeriod)) {
      periodsUsed = [{ period: grantedPeriod || noPoolPeriod, usedHours }];
    }
    if (periodsUsed.length && computedHours > 0) {
      periodsUsed = scalePeriodsUsed(periodsUsed, computedHours, usedHours, utils);
    }

    const primaryPeriod = (periodsUsed[0] && periodsUsed[0].period) || grantedPeriod || poolPeriod || (discountResult.isUnlimited ? noPoolPeriod : null) || null;

    return {
      status: 'success',
      // Members pay the day's own rate; the checkout function must never swap in weekday_price.
      useWeekendPrice: false,
      discount: Number(discountPercent.toFixed(DISCOUNT_DECIMALS)),
      usedHours,
      period: primaryPeriod,
      periodsUsed: periodsUsed.map(({ period, usedHours: used }) => ({ period, usedHours: utils.roundHours(used) })),
      daysUsed: buildDaysUsed(discountResult.hoursByDay, usedHours, utils)
    };
  }

  /**
   * Hours per facility-local day, so a booking over midnight is charged against both days'
   * allowances when the next cart item is priced. Keyed by YMD — one membership, one facility.
   */
  function buildDaysUsed(hoursByDay, usedHours, utils) {
    if (!hoursByDay || typeof hoursByDay !== 'object') return null;
    const dayKeys = Object.keys(hoursByDay);
    if (!dayKeys.length) return null;

    const raw = dayKeys.map((dayKey) => Number(hoursByDay[dayKey] || 0));
    const total = raw.reduce((sum, hours) => sum + hours, 0);
    if (!(total > 0)) return null;

    // Scale to what the line item actually spends, then repair the rounding drift.
    const scaled = raw.map((hours) => (hours * usedHours) / total);
    const rounded = roundPartsToTotal(scaled, usedHours, utils);

    const daysUsed = {};
    dayKeys.forEach((dayKey, index) => {
      const ymd = dayKey.includes('|') ? dayKey.slice(dayKey.indexOf('|') + 1) : dayKey;
      if (rounded[index] > 0) daysUsed[ymd] = (daysUsed[ymd] || 0) + rounded[index];
    });
    return Object.keys(daysUsed).length ? daysUsed : null;
  }

  const api = {
    getProMembershipPeriodKey,
    getMembershipCoverageForDate,
    resolveTierEligibility,
    resolveTierDiscount,
    getMembershipEligibilityForSlot,
    readMembershipPreference,
    publishMembershipPreference,
    resolvePreferredType,
    beginMembershipPreferenceLookup,
    endMembershipPreferenceLookup,
    isMembershipPreferencePending,
    readMembershipRowFacilityId,
    rankAccountMemberships,
    getCoveringPeriod,
    getMembershipHoursForDate,
    getMembershipHoursUsedInCart,
    readConsumableHours,
    reconcileConsumableHours,
    readSlotsMembershipMeta,
    readHoldMembership,
    buildBackendGrant,
    buildMembershipHoursCartProperty
  };

  if (typeof window !== 'undefined') window.MembershipSlotPricing = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
